import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Autocomplete } from '@mui/material';
import { Grid, TextField, FormGroup, FormControlLabel, Checkbox, Button, Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import Api from '../../services/api';
import { MarketingChannelsInvites } from './marketingChannelsInvites';
import { RegisteredParnerInvites } from './registeredParnerInvites';
import { InfluencerChannel } from './influencerChannel';
import { marketingChannelOptions } from '../../utils/marketingChannels';
import { Title } from '../../components/atoms/title';
import { useUniversities } from '../../hooks/universities';

export function PartnerInvites() {
  const { partners, countries } = useUniversities();
  const [channel, setChannel] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkCreateError, setBulkCreateError] = useState(null);

  const [createOption, setCreateOption] = useState({
    single: true,
    bulk: false,
  });

  useEffect(() => {
    setSelectedCountry(countries.find(c => c.name === 'Brazil')?.id);
  }, [countries]);

  const { reset } = useForm();

  const handleChangeCreateOption = event => {
    setCreateOption({
      [event.target.name === 'single' ? 'bulk' : 'single']: false,
      [event.target.name]: event.target.checked,
    });
  };

  const [file, setFile] = useState();

  const handleFileChange = e => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const formData = {
    selectedCountry,
    channel,
  };

  const handleCountriesAutocompleteSelection = (_e, value, reason) => {
    if (reason === 'clear') {
      setSelectedCountry(null);
      return;
    }

    if (value !== null) {
      setSelectedCountry(value.id);
    }
  };

  const handleBulkCreateInvite = async csv => {
    try {
      setLoading(true);
      setBulkCreateError(null);
      const bulkFormData = new FormData();
      bulkFormData.append('file', csv);

      await Api.post('/invite/create_bulk', bulkFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      reset();
      toast('Convites criados com sucesso!', { type: 'success' });
    } catch (error) {
      if (error.response?.data?.error) {
        const errorMessage = error.response.data.error.message;
        setBulkCreateError(errorMessage);
      } else if (error.response?.data) {
        const errorMessage = error.response.data;
        setBulkCreateError(errorMessage);
      } else {
        toast(error.message, { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Title>Geração de convites</Title>
      </Grid>

      <Grid item xs={12} md={12} style={{ padding: '8px 16px' }}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={createOption.single}
                onChange={handleChangeCreateOption}
                name='single'
                color='primary'
              />
            }
            label='Convite Único'
          />
          <FormControlLabel
            control={
              <Checkbox checked={createOption.bulk} onChange={handleChangeCreateOption} name='bulk' color='primary' />
            }
            label='Múltiplos convites'
          />
        </FormGroup>
      </Grid>

      {createOption.bulk && (
        <Grid item xs={12} textAlign='center'>
          <Box m='auto' mt={3}>
            <Button variant='contained' component='label'>
              Fazer upload de arquivo .csv
              <input type='file' accept='.csv' hidden onChange={handleFileChange} />
            </Button>
          </Box>

          {file && (
            <Typography variant='inherit' color='textSecondary'>
              {file.name}
            </Typography>
          )}

          <Box m='auto' mt={3} mb={3}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              onClick={() => handleBulkCreateInvite(file)}
              disabled={!file}
              loading={loading}
            >
              Criar
            </Button>
          </Box>
          {bulkCreateError && (
            <Alert severity='error'>
              <AlertTitle>Erro</AlertTitle>
              <pre style={{ fontFamily: 'Montserrat', fontSize: 14 }}>{bulkCreateError}</pre>
            </Alert>
          )}
        </Grid>
      )}

      {createOption.single && (
        <>
          <Grid xs={12} md={6} item>
            <Autocomplete
              required
              onChange={handleCountriesAutocompleteSelection}
              options={countries}
              getOptionLabel={option => option?.name}
              defaultValue={{ name: 'Brazil', id: 1 }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              fullWidth
              variant='outlined'
              label='País'
              renderInput={params => <TextField {...params} variant='outlined' label='País' />}
            />
          </Grid>
          <Grid xs={12} md={12} item>
            <Autocomplete
              value={channel}
              options={marketingChannelOptions}
              getOptionLabel={option => option}
              isOptionEqualToValue={option => option}
              onChange={(_e, option) => {
                if (option) {
                  setChannel(option);
                } else {
                  setChannel(null);
                }
              }}
              renderInput={params => (
                <TextField style={{ width: '100%' }} autoFocus {...params} label='Canal' variant='outlined' />
              )}
            />
          </Grid>

          {channel === 'Influencers' ? (
            <InfluencerChannel formData={formData} partners={partners} />
          ) : channel !== 'Existing Platforms' ? (
            <RegisteredParnerInvites formData={formData} partners={partners} />
          ) : (
            <MarketingChannelsInvites formData={formData} partners={partners} />
          )}
        </>
      )}
    </Grid>
  );
}
